if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', class MegaMenu extends HTMLElement {
    constructor() {
      super();
      this.currentOpenMenu = null;
      this.isMultirow = false;
      this.header = document.querySelector('.section-header');
      this.stickyHeader = document.querySelector('sticky-header');
      this.menuContainerParent = document.querySelector('.mega-menu-container');
      this.menuContainer = document.querySelector('.mega-menu__wrapper');
      this.inlineMenu = document.querySelector('.header__inline-menu');
      this.menuBackdrop = document.querySelector('.mega-menu__backdrop');

      this.badgesData = [];
      this.menuHeights = {};
      this.badgesSignature = '';
      this.badgesInitialized = false;
      this.badgesPositionTimers = new Map();
      this.menuOffsetCache = new WeakMap();
      this.rootMenuOffsetCache = null;
      this.dropdownWidthCache = new WeakMap();
      this.dropdownHeightCache = new WeakMap();
      this.preloadedMediaUrls = new Set();
      this.idlePreloadHandle = null;
      this.lastLayoutWidth = 0;
      this.lastInlineHeight = 0;
      this.lastMenuOffset = null;
      this.resizeRaf = null;
      this.resizeDebounce = null;
      this.initialLayoutRaf = null;
      this.openedByKeyboard = false;

      this.animationConfig = {
        easing: 'cubic-bezier(0, 0, 0.12, 0.88)',
        duration: parseFloat(this.getAttribute('data-duration')) || 0.5,
        stageDuration: parseFloat(this.getAttribute('data-stager-duration')) || 0.2,
        stageDelay: parseFloat(this.getAttribute('data-stager-delay')) || 0.05,
        hoverDelay: 100
      };

      this.motion = window.motion || {};

      this.classList.add('mega-menu--initialized');

      const allMenuButtons = this.querySelectorAll('[data-menu]');

      this.megaMenuButtons = [];
      this.dropdownButtons = [];

      this.allMenuContents = Array.from(document.querySelectorAll('[data-menu-content]'));
      this.allDropdownButtons = Array.from(document.querySelectorAll('.has-dropdown, .dropdown-menu'));
      this.allListMenus = Array.from(document.querySelectorAll('.list-menu'));

      allMenuButtons.forEach(button => {
        const menuValue = button.getAttribute('data-menu');
        const hasMegaContent = document.querySelector(`[data-menu-content="${menuValue}"]`) !== null;
        const parentSimple = button.closest('.simple-menu');

        if (hasMegaContent) {
          this.megaMenuButtons.push(button);
          if (parentSimple) {
            parentSimple.classList.remove('simple-menu');
            parentSimple.classList.add('dropdown-menu');
          }
        } else {
          !parentSimple && this.dropdownButtons.push(button);
        }
      });

      this.eventsMegamenu();
      this.eventsDropdown();

      this.loadBadgesData();
      this.initializeBadges();

      if (!this.menuContainerParent) return;

      if (this.inlineMenu) {
        this.handleWindowResize = () => {
          if (this.resizeDebounce) clearTimeout(this.resizeDebounce);
          this.resizeDebounce = setTimeout(() => {
            this.resizeDebounce = null;
            if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
            this.resizeRaf = requestAnimationFrame(() => {
              this.resizeRaf = null;
              this.invalidateLayoutCaches();
              this.updateMenuLayout();
            });
          }, 120);
        };

        window.addEventListener('resize', this.handleWindowResize);
        this.updateMenuLayout();
      }

      this.scheduleIdleMediaPreload();

      setTimeout(() => {
        const menuVideos = this.menuContainer.querySelectorAll('deferred-media');
        menuVideos.forEach(video => {
          this.pauseDeferredMedia(video);
        });
      }, 100);

      this.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ESCAPE') {
          this.escapeCloseAction();
        }
      });
    }

    escapeCloseAction() {
      const activeBtn = this.inlineMenu.querySelector('[data-menu].active') || this.inlineMenu.querySelector('[aria-expanded="true"]');
      this.forceClose();
      if (activeBtn) {
        activeBtn.classList.remove('active');
        activeBtn.setAttribute('aria-expanded', 'false');
        activeBtn.focus();
      }
    }

    pauseDeferredMedia(mediaEl) {
      if (!mediaEl) return;
      if (typeof mediaEl.pauseMedia === 'function') {
        mediaEl.pauseMedia();
        return;
      }
      const video = mediaEl.querySelector?.('video');
      if (video && typeof video.pause === 'function') {
        video.pause();
      }
    }

    restartDeferredMedia(mediaEl) {
      if (!mediaEl || typeof mediaEl.restartMedia !== 'function') return;
      mediaEl.restartMedia();
    }

    updateMenuLayout() {
      if (window.innerWidth < 1025) return;
      const width = window.innerWidth;
      const inlineHeight = this.inlineMenu?.offsetHeight || 0;
      const prevInlineHeight = this.lastInlineHeight;
      if (width === this.lastLayoutWidth && inlineHeight === this.lastInlineHeight) return;

      this.lastLayoutWidth = width;
      this.lastInlineHeight = inlineHeight;
      this.isMultirow = inlineHeight > 60;
      this.invalidateOffsetCache();
      this.classList.toggle('mega-menu--multirow', this.isMultirow);
      this.precomputeMenuOffsets();
      this.setMenuOffset();
      DeviceUtils.updateHeaderSizes();
      if (this.stickyHeader?.setHeaderHeight && inlineHeight !== prevInlineHeight) {
        this.stickyHeader.setHeaderHeight();
      }
    }

    setMenuOffset(button = null) {
      const parentGrid = this.closest('.section-header');
      if (!parentGrid) return;
      const targetElement = this.isMultirow ? button || this : this;
      const nextOffset = this.getMenuOffsetValue(parentGrid, targetElement, button);
      if (nextOffset === this.lastMenuOffset) return;
      this.lastMenuOffset = nextOffset;
      if (nextOffset) {
        this.menuContainerParent.style.setProperty('--megamenu-offset', nextOffset);
        return;
      }
      this.menuContainerParent.style.removeProperty('--megamenu-offset');
    }

    loadBadgesData() {
      try {
        const scriptElement = document.getElementById('navigation-badges-data');
        if (scriptElement) {
          let jsonText = scriptElement.textContent.trim();
          jsonText = jsonText.replace(/,(\s*[}\]])/g, '$1');
          this.badgesData = JSON.parse(jsonText);
        }
      } catch (error) {
        this.badgesData = [];
      }
    }

    findMatchingBadges(itemText) {
      const matchingBadges = [];
      const lowerItemText = itemText.toLowerCase();

      for (const badge of this.badgesData) {
        if (badge.items && Array.isArray(badge.items)) {
          const hasMatch = badge.items.some(item =>
            item.toLowerCase() === lowerItemText
          );

          if (hasMatch) {
            matchingBadges.push(badge);
            if (matchingBadges.length >= 2) break;
          }
        }
      }

      return matchingBadges;
    }

    initializeBadges() {
      const scope = this.closest('.section-header') || document;
      const menuLinks = scope.querySelectorAll('.list-menu__item, .mega-menu__link, .menu-drawer__menu-item, .nav-tabs__item');
      const signature = `${this.badgesData.length}:${menuLinks.length}`;
      if (this.badgesInitialized && signature === this.badgesSignature) return;

      this.badgesSignature = signature;
      this.badgesInitialized = true;
      this.clearBadges();

      menuLinks.forEach(link => {
        const linkSpan = link.querySelector('span:not(.menu-badges__badge)');
        if (!linkSpan) return;

        const itemText = linkSpan.textContent.trim();
        let matchingBadges = this.findMatchingBadges(itemText);

        if (matchingBadges.length > 0) {
          const badgesHTML = matchingBadges
            .map(badge => {
              const badgeText = badge.text;
              const badgeStyle = badge.style ? ` style="${badge.style}"` : '';
              return `<span class="menu-badges__badge"${badgeStyle}>${badgeText}</span>`;
            })
            .join('');

          const isLevelFirst = link.classList.contains('mega-menu__link--level-1') || link.classList.contains('menu-drawer__menu-item') || link.classList.contains('menu-drawer__menu-item');
          const hasDropdown = link.parentElement.classList.contains('has-dropdown');

          const badgeContainer = isLevelFirst
            ? `<div class="menu-badges">${badgesHTML}</div>`
            : `<menu-badges><div class="menu-badges">${badgesHTML}</div></menu-badges>`;

          const targetElement = hasDropdown && !isLevelFirst ? linkSpan : link;
          const position = isLevelFirst ? 'afterbegin' : 'beforeend';

          targetElement.insertAdjacentHTML(position, badgeContainer);

          const parentContainer = link.closest('.list-menu, [data-menu-content]');
          if (parentContainer) {
            parentContainer.setAttribute('data-has-badges', 'true');
          }
        }
      });

      this.invalidateMenuContentCaches();
      if (this.initialLayoutRaf) cancelAnimationFrame(this.initialLayoutRaf);
      this.initialLayoutRaf = requestAnimationFrame(() => {
        this.initialLayoutRaf = null;
        this.updateMenuLayout();
      });
    }

    clearBadges() {
      const existingBadges = this.querySelectorAll('.menu-badges');
      existingBadges.forEach(badge => badge.remove());
      const menuBadgesElements = this.querySelectorAll('menu-badges');
      menuBadgesElements.forEach(element => element.remove());
      this.querySelectorAll('[data-has-badges]').forEach(el => el.removeAttribute('data-has-badges'));
    }

    updateBadgesPosition(container, selector = 'menu-badges', updateHeight = true, delay = 200) {
      if (!container.hasAttribute('data-has-badges') && !container.querySelector('[data-has-badges]')) return;

      const previousTimer = this.badgesPositionTimers?.get(container);
      if (previousTimer) clearTimeout(previousTimer);

      const runUpdate = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const badges = container.querySelectorAll(selector);
            badges.forEach(badge => {
              if (typeof badge.updatePosition === 'function') {
                badge.updatePosition();
              }
            });

            if (this.currentOpenMenu && updateHeight) {
              this.invalidateMenuContentCaches();
              this.menuContainer.style.height = this.getMenuHeight(this.currentOpenMenu) + 'px';
            }
          });
        });
      };

      if (delay === 0) {
        runUpdate();
      } else {
        const timer = setTimeout(runUpdate, delay);
        this.badgesPositionTimers?.set(container, timer);
      }
    }

    invalidateOffsetCache() {
      this.menuOffsetCache = new WeakMap();
      this.rootMenuOffsetCache = null;
      this.lastMenuOffset = null;
    }

    invalidateLayoutCaches() {
      this.invalidateMenuContentCaches();
      this.invalidateOffsetCache();
    }

    invalidateMenuContentCaches() {
      this.menuHeights = {};
      this.scrollStateCache = {};
      this.dropdownWidthCache = new WeakMap();
      this.dropdownHeightCache = new WeakMap();
    }

    scheduleIdleMediaPreload() {
      if (this.idlePreloadHandle || !this.menuContainerParent) return;

      const preloadTask = () => {
        this.idlePreloadHandle = null;
        this.preloadMenuMedia();
      };

      if ('requestIdleCallback' in window) {
        this.idlePreloadHandle = window.requestIdleCallback(preloadTask, { timeout: 1800 });
        return;
      }

      this.idlePreloadHandle = window.setTimeout(preloadTask, 900);
    }

    preloadMenuMedia() {
      const mediaNodes = document.querySelectorAll(
        '[data-menu-content] img, [data-menu-content] source[srcset], [data-menu-content] video[poster]'
      );

      mediaNodes.forEach((node) => {
        if (node.tagName === 'IMG') {
          this.preloadImageNode(node);
          return;
        }

        if (node.tagName === 'SOURCE') {
          this.preloadSourceNode(node);
          return;
        }

        const poster = node.getAttribute('poster');
        if (poster) {
          this.preloadUrl(poster);
        }
      });
    }

    preloadImageNode(image) {
      const src = image.currentSrc || image.getAttribute('src') || image.dataset.src;
      const srcset = image.getAttribute('srcset') || image.dataset.srcset;

      if (srcset) {
        this.preloadUrl(srcset, 'image', image.getAttribute('sizes') || '100vw', 'srcset');
      }
      if (src) {
        this.preloadUrl(src);
      }
    }

    preloadSourceNode(source) {
      const srcset = source.getAttribute('srcset') || source.dataset.srcset;
      if (!srcset) return;
      this.preloadUrl(srcset, 'image', source.getAttribute('sizes') || '100vw', 'srcset');
    }

    preloadUrl(url, as = 'image', sizes = '100vw', mode = 'src') {
      if (!url || this.preloadedMediaUrls.has(url)) return;
      this.preloadedMediaUrls.add(url);

      if (mode === 'srcset') {
        const image = new Image();
        image.sizes = sizes;
        image.srcset = url;
        return;
      }

      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = as;
      link.href = url;
      link.fetchPriority = 'low';
      document.head.appendChild(link);
    }

    precomputeMenuOffsets() {
      const parentGrid = this.closest('.section-header');
      if (!parentGrid) return;

      this.rootMenuOffsetCache = this.buildOffsetCacheEntry(parentGrid, this, null);
      this.megaMenuButtons.forEach((button) => {
        this.menuOffsetCache.set(button, this.buildOffsetCacheEntry(parentGrid, button, button));
      });
    }

    buildOffsetCacheEntry(parentGrid, targetElement, button = null) {
      if (!targetElement) return { width: window.innerWidth, multirow: this.isMultirow, value: '' };

      const parentRect = parentGrid.getBoundingClientRect();
      const targetRect = targetElement.getBoundingClientRect();
      const offsetAdjustment = button ? 5 : 10;
      const targetBottom = targetRect.bottom - parentRect.top;
      const offset = parentRect.height - targetBottom - offsetAdjustment;

      return {
        width: window.innerWidth,
        multirow: this.isMultirow,
        value: offset > 15 ? `-${offset}px` : ''
      };
    }

    getMenuOffsetValue(parentGrid, targetElement, button = null) {
      if (!targetElement) return '';

      const cacheSource = targetElement === this ? this.rootMenuOffsetCache : this.menuOffsetCache.get(targetElement);
      if (
        cacheSource &&
        cacheSource.width === window.innerWidth &&
        cacheSource.multirow === this.isMultirow
      ) {
        return cacheSource.value;
      }

      const cacheEntry = this.buildOffsetCacheEntry(parentGrid, targetElement, button);

      if (targetElement === this) {
        this.rootMenuOffsetCache = cacheEntry;
      } else {
        this.menuOffsetCache.set(targetElement, cacheEntry);
      }

      return cacheEntry.value;
    }

    openMegaMenuStatic(index) {
      const dropdownContent = this.menuContainer;
      const target = document.querySelector(`[data-menu-content="${index}"]`);
      if (!target) return;

      const newHeight = this.getMenuHeight(index);
      dropdownContent.style.height = `${newHeight}px`;

      this.menuBackdrop.style.opacity = '1';

      this.allMenuContents.forEach(el => el.classList.remove('active'));
      target.classList.add('active');
      dropdownContent.classList.add('open');

      this.updateBadgesPosition(target, 'menu-badges', false, 0);

      const menuVideos = dropdownContent.querySelectorAll('deferred-media');
      menuVideos.forEach(video => {
        video.loadContent?.();
        video.playMedia?.();
      });

      this.setScrollClass(dropdownContent);
      this.currentOpenMenu = index;

      if (this.openedByKeyboard) {
        trapFocus(this.menuContainer, target.querySelector('a, button'));
      }
    }

    openMenuForEditor(menuIndex) {
      this.classList.remove('mega-menu--block');
      document.body.removeAttribute('data-mega-menu-block');
      this.forceClose();
      this.classList.add('mega-menu--block');
      document.body.setAttribute('data-mega-menu-block', 'true');

      const menuButton = document.querySelector(`[data-menu="${menuIndex}"]`);
      if (menuButton) {
        this.updateButtonState(menuButton, true, true);
        this.setMenuOffset(menuButton);
        this.openMegaMenuStatic(menuIndex);
      }
    }

    eventsMegamenu() {
      this.megaMenuButtons.forEach(button => {
        button.classList.add('mega-menu__link');

        button.addEventListener('click', (e) => {
          const slotNumber = button.dataset.menu;
          if (this.currentOpenMenu === slotNumber) {
            this.closeMegaMenu();
          } else {
            e.preventDefault();
            this.openedByKeyboard = e.detail === 0;
            this.lastTrigger = button;
            this.closeOtherPopup();
            this.updateButtonState(button, true, true);
            this.setMenuOffset(button);
            this.openMegaMenu(slotNumber);
          }
        });

        button.addEventListener('mouseenter', () => {
          this.openedByKeyboard = false;
          this.closeOtherPopup();
          this.header.classList.add('has-hovered-megamenu');
          this.header.classList.remove('has-hovered-dropdown');
          this.isHovering = true;
          const slotNumber = button.dataset.menu;

          if (this.classList.contains('mega-menu--block')) {
            this.updateButtonState(button, true, true);
            this.setMenuOffset(button);
            this.openMegaMenu(slotNumber);
            return;
          }

          if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
          }

          this.hoverTimeout = setTimeout(() => {
            if (this.isHovering) {
              this.updateButtonState(button, true, true);
              if (this.currentOpenMenu !== slotNumber) {
                this.setMenuOffset(button);
                this.openMegaMenu(slotNumber);
              }
            }
          }, 150);
        });

        button.addEventListener('mouseleave', () => {
          if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
            this.hoverTimeout = null;
          }

          this.isHovering = false;

          if (this.classList.contains('mega-menu--block')) {
            return;
          }

          setTimeout(() => {
            if (!this.isHovering) {
              this.closeMegaMenu();
            }
          }, 100);
        });

        button.addEventListener('focusin', (e) => {
          e.stopPropagation();
          if (this.currentOpenMenu === button.dataset.menu) return;
          this.openedByKeyboard = true;
          this.lastTrigger = button;
          const slotNumber = button.dataset.menu;
          this.closeAllDropdowns();
          this.updateButtonState(button, true, true);
          this.setMenuOffset(button);
          requestAnimationFrame(() => {
            this.openMegaMenu(slotNumber);
          });
        });

        button.addEventListener('focusout', (e) => {
          if (!this.openedByKeyboard) return;
          const nextFocusedButton = e.relatedTarget?.closest?.('[data-menu]');

          if (e.relatedTarget &&
            !button.contains(e.relatedTarget) &&
            !this.menuContainer.contains(e.relatedTarget) &&
            !nextFocusedButton) {
            this.closeMegaMenu();
          }
        });
      });

      this.menuContainer.addEventListener('mouseenter', () => {
        if (this.menuContainerParent._isClosing) return;
        this.isHovering = true;
      });

      this.menuContainer.addEventListener('mouseleave', () => {
        if (this.menuContainerParent._isClosing || this.currentOpenMenu == null) return;

        if (this.classList.contains('mega-menu--block')) {
          return;
        }

        this.isHovering = false;
        setTimeout(() => {
          if (!this.isHovering) {
            this.closeMegaMenu();
          }
        }, this.animationConfig.hoverDelay);
      });

      this.menuContainer.addEventListener('focusout', (e) => {
        const activeBtn = this.querySelector('[data-menu].active');
        if (e.relatedTarget && !this.menuContainer.contains(e.relatedTarget) && (activeBtn && !activeBtn.contains(e.relatedTarget))) {
          this.closeMegaMenu();
        }
      });
    }

    openMegaMenu(index) {
      if (this.classList.contains('mega-menu--block')) {
        this.openMegaMenuStatic(index);
        return;
      }
      if (this.menuBackdrop && document.documentElement.scrollHeight <= document.documentElement.clientHeight) {
        document.body.classList.add('overflow-hidden-force');
      }
      const dropdownContent = this.menuContainer;
      const target = document.querySelector(`[data-menu-content="${index}"]`);
      if (!target) return;

      if (dropdownContent._heightAnimation) {
        dropdownContent._heightAnimation.cancel();
      }

      const currentHeight = dropdownContent.offsetHeight;
      this.updateBadgesPosition(target, 'menu-badges', false, 50);

      const newHeight = this.getMenuHeight(index);
      let startHeight = currentHeight;

      if (!dropdownContent.classList.contains('open')) {
        startHeight = newHeight * 0.75;
        dropdownContent.style.height = `${startHeight}px`;
      }

      const baseDuration = this.animationConfig.duration;
      const duration = Math.min(baseDuration * 1.5,
        Math.max(0.25, Math.abs(newHeight - startHeight) * baseDuration / 300));

      const menuVideos = dropdownContent.querySelectorAll('deferred-media');
      menuVideos.forEach(video => {
        video.loadContent?.();
        video.playMedia?.();
      });

      const animationProps = !dropdownContent.classList.contains('open')
        ? { height: [`${startHeight}px`, `${newHeight}px`], opacity: [0.5, 1] }
        : { height: [`${startHeight}px`, `${newHeight}px`] };

      dropdownContent._heightAnimation = this.motion.animate(dropdownContent, animationProps, {
        duration: duration,
        easing: this.animationConfig.easing
      });

      if (this.menuBackdrop) {
        if (this.currentOpenMenu === null || !dropdownContent.classList.contains('open')) {
          this.menuBackdrop.style.display = 'block';
          requestAnimationFrame(() => {
            this.motion.animate(this.menuBackdrop, {
              opacity: ['0', '1']
            }, {
              duration: duration,
              easing: this.animationConfig.easing
            });
          });
        }
      }

      this.allMenuContents.forEach(el => el.classList.remove('active'));
      target.classList.add('active');

      if (this.currentOpenMenu !== index) {
        this.updateBadgesPosition(target, 'menu-badges');
      }

      dropdownContent._heightAnimation.finished.finally(() => {
        dropdownContent.classList.add('open');
        this.setScrollClass(dropdownContent);
        dropdownContent._heightAnimation = null;
        if (this.openedByKeyboard) {
          const container = target.querySelector('.mega-menu__grid');
          requestAnimationFrame(() => {
            trapFocus(container, false, this, this.lastTrigger);
          });
        }
      });

      this.currentOpenMenu = index;
    }

    closeMegaMenu() {
      if (this.classList.contains('mega-menu--block')) return;

      removeTrapFocus?.();

      this.menuContainerParent._isClosing = true;
      const dropdownContent = this.menuContainer;
      dropdownContent.classList.remove('has-scroll');

      if (dropdownContent._heightAnimation) {
        dropdownContent._heightAnimation.cancel();
      }

      const menuAnimation = this.motion.animate(dropdownContent, {
        opacity: ['1', '0']
      }, {
        duration: this.animationConfig.duration,
        easing: this.animationConfig.easing
      });

      const opacityAnimation = this.motion.animate(this.menuContainerParent, {
        opacity: ['1', '0']
      }, {
        duration: this.animationConfig.duration,
        easing: this.animationConfig.easing
      });

      menuAnimation.finished.finally(() => {
        dropdownContent.classList.remove('open');
        dropdownContent.style.opacity = '';
        dropdownContent.style.height = '';
        if (this.menuBackdrop && this.currentOpenMenu == null) {
          this.menuBackdrop.style.opacity = '';
          this.menuBackdrop.style.display = 'none';
          document.body.classList.remove('overflow-hidden-force');
        }
        this.menuContainerParent.style.opacity = '';
        if (this.header) {
          this.header.classList.remove('has-hovered-megamenu', 'has-hovered-dropdown');
        }
        this.menuContainerParent._isClosing = false;

        const menuVideos = dropdownContent.querySelectorAll('deferred-media');
        menuVideos.forEach(video => {
          this.pauseDeferredMedia(video);
          this.restartDeferredMedia(video);
        });
      });

      this.allMenuContents.forEach(content => content.classList.remove('active'));
      this.currentOpenMenu = null;
      removeTrapFocus?.();
      if (this.openedByKeyboard && this.lastTrigger) {
        requestAnimationFrame(() => {
          let li = this.lastTrigger.closest('li');
          let nextLi = li.nextElementSibling;
          if (nextLi) {
            nextLi.querySelector('a, button')?.focus();
          } else {
            const menu = this.lastTrigger.closest('.header__inline-menu');
            if (menu) menu.focus();
          }
        });
      }
      this.openedByKeyboard = false;
      this.updateButtonState(null, false, true);
    }

    setScrollClass(dropdown = this.menuContainer) {
      const cacheKey = this.currentOpenMenu || 'default';

      if (this.scrollStateCache[cacheKey]) {
        const cached = this.scrollStateCache[cacheKey];
        this.menuContainer.style.height = cached.height;
        dropdown.classList.toggle('has-scroll', cached.hasScroll);
        return;
      }

      this.menuContainer.style.height = dropdown.scrollHeight + 'px';
      const hasScroll = dropdown.scrollHeight > dropdown.clientHeight;
      dropdown.classList.toggle('has-scroll', hasScroll);

      this.scrollStateCache[cacheKey] = {
        height: dropdown.scrollHeight,
        hasScroll: hasScroll
      };
    }

    updateButtonState(element, isOpen, isMegaMenu = false) {
      if (isMegaMenu) {
        this.megaMenuButtons.forEach(btn => {
          btn.classList.remove('active');
          btn.setAttribute('aria-expanded', 'false');
        });

        if (element) {
          element.classList.add('active');
          element.setAttribute('aria-expanded', 'true');
        }
      } else {
        if (element && element.getAttribute('role') !== 'none') element.setAttribute('aria-expanded', isOpen.toString());
      }
    }

    eventsDropdown() {
      if (this.dropdownButtons.length < 1) return;

      const allDropdownParents = this.dropdownButtons
        .filter(button => button.closest?.('.dropdown-menu'))
        .concat(Array.from(document.querySelectorAll('.has-dropdown')));

      allDropdownParents.forEach(parent => {
        let dropdownContent;

        if (parent.classList.contains('dropdown-menu')) {
          dropdownContent = parent.querySelector('.list-menu');
        } else {
          dropdownContent = parent.nextElementSibling?.classList?.contains('list-menu')
            ? parent.nextElementSibling
            : parent.querySelector('.list-menu');
        }

        if (!dropdownContent) return;

        const animateHeight = dropdownContent.classList.contains('list-menu--level-2');
        let isAnimating = false;
        let animation = null;

        const cleanUp = () => {
          if (animation) {
            animation.cancel();
            animation = null;
          }
          if (dropdownContent._heightAnimation) {
            dropdownContent._heightAnimation.cancel();
            dropdownContent._heightAnimation = null;
          }
          isAnimating = false;
        };

        const openDropdown = () => {
          if (dropdownContent.classList.contains('active')) return;

          allDropdownParents.forEach(otherParent => {
            if (otherParent === parent || otherParent.contains(parent)) return;

            const otherContent = otherParent.classList.contains('dropdown-menu')
              ? otherParent.querySelector('.list-menu')
              : (otherParent.nextElementSibling?.classList?.contains('list-menu') ? otherParent.nextElementSibling : otherParent.querySelector('.list-menu'));

            if (otherContent && otherContent.classList.contains('active')) {
              if (otherContent.contains(parent)) return;

              this.clearTimers(otherParent);
              if (otherContent._heightAnimation) {
                otherContent._heightAnimation.cancel();
                otherContent._heightAnimation = null;
              }
              otherContent.classList.remove('active');
              this.updateButtonState(otherParent, false);
              Object.assign(otherContent.style, { opacity: '', height: '', transform: '' });
            }
          });

          if (this.classList.contains('mega-menu--block') && parent.classList.contains('mega-menu__link--level-1')) {
            this.classList.remove('mega-menu--block');
            document.body.removeAttribute('data-mega-menu-block');
            this.forceClose();
          }

          if (animateHeight) {
            this.clearTimers(parent);
            const delay = parent.classList.contains('has-dropdown') ? 0 : this.animationConfig.hoverDelay;

            parent._openTimer = setTimeout(() => {
              if (isAnimating) return;

              cleanUp();
              isAnimating = true;

              if (dropdownContent.hasAttribute('data-has-badges')) {
                this.updateBadgesPosition(dropdownContent, ':scope > li > a menu-badges', false, 0);
                this.dropdownWidthCache.delete(dropdownContent);
                this.dropdownHeightCache.delete(dropdownContent);
                Object.assign(dropdownContent.style, { visibility: '', display: '', height: '' });
              }

              this.positionDropdown(parent, dropdownContent);
              dropdownContent.classList.add('active');
              this.updateButtonState(parent, true);
              this.animateDropdown(dropdownContent);

              setTimeout(() => {
                isAnimating = false;
              }, 30);
            }, delay);
          } else {
            this.dropdownWidthCache.delete(dropdownContent);
            this.dropdownHeightCache.delete(dropdownContent);
            this.positionDropdown(parent, dropdownContent);
            dropdownContent.classList.add('active');
            this.updateButtonState(parent, true);
            this.updateBadgesPosition(dropdownContent, ':scope > li > a menu-badges', false, 0);
            isAnimating = false;
          }
        };

        const closeDropdown = () => {
          if (!animateHeight) {
            dropdownContent.classList.remove('active');
            this.updateButtonState(parent, false);
            return;
          }

          this.clearTimers(parent);

          parent._closeTimer = setTimeout(() => {
            if (!dropdownContent.classList.contains('active') || isAnimating) return;

            cleanUp();
            isAnimating = true;

            this.animateDropdown(dropdownContent, true);

            if (dropdownContent._heightAnimation) {
              dropdownContent._heightAnimation.finished.then(() => {
                dropdownContent.classList.remove('active');
                this.updateButtonState(parent, false);
                isAnimating = false;
              }).catch(() => {
                isAnimating = false;
              });
            } else {
              dropdownContent.classList.remove('active');
              this.updateButtonState(parent, false);
              isAnimating = false;
            }
          }, 100);
        };

        parent.addEventListener('mouseenter', () => {
          this.clearTimers(parent);

          let ancestor = parent.parentElement?.closest('.has-dropdown, .dropdown-menu');
          while (ancestor) {
            this.clearTimers(ancestor);
            ancestor = ancestor.parentElement?.closest('.has-dropdown, .dropdown-menu');
          }

          if (!parent._badgesWarmed && dropdownContent.hasAttribute('data-has-badges')) {
            parent._badgesWarmed = true;
            const savedStyles = {
              visibility: dropdownContent.style.visibility,
              display: dropdownContent.style.display,
              position: dropdownContent.style.position,
              pointerEvents: dropdownContent.style.pointerEvents,
              opacity: dropdownContent.style.opacity
            };
            Object.assign(dropdownContent.style, {
              visibility: 'hidden',
              display: 'block',
              position: 'fixed',
              pointerEvents: 'none',
              opacity: '0'
            });
            dropdownContent.querySelectorAll('menu-badges').forEach(b => {
              if (typeof b.updatePosition === 'function') {
                if (b._updateRaf) cancelAnimationFrame(b._updateRaf);
                if (b._updateTimer) clearTimeout(b._updateTimer);
                if (b.offsetWidth) b.updatePosition(0);
              }
            });
            this.dropdownWidthCache.delete(dropdownContent);
            this.dropdownHeightCache.delete(dropdownContent);
            Object.assign(dropdownContent.style, savedStyles);
          }

          this.closeOtherPopup();
          if (parent.classList.contains('mega-menu__link--level-1')) {
            this.header.classList.remove('has-hovered-megamenu');
            this.header.classList.add('has-hovered-dropdown');
          }
          openDropdown();
        });

        parent.addEventListener('mouseleave', (e) => {
          if (e.relatedTarget && (dropdownContent.contains(e.relatedTarget) || parent.contains(e.relatedTarget))) {
            return;
          }
          closeDropdown();
        });

        dropdownContent.addEventListener('mouseenter', () => {
          let current = parent;
          while (current) {
            this.clearTimers(current);
            current = current.parentElement?.closest('.has-dropdown, .dropdown-menu');
          }
        });

        dropdownContent.addEventListener('mouseleave', (e) => {
          if (!parent.contains(e.relatedTarget)) {
            closeDropdown();
          }
        });

        parent.addEventListener('click', (e) => {
          if (e.detail > 0 && !parent.classList.contains('has-dropdown')) return;

          if (dropdownContent.classList.contains('active')) {
            closeDropdown();
          } else {
            if (parent.classList.contains('has-dropdown')) e.preventDefault();
            this.closeOtherPopup();
            openDropdown();
          }
        });

        parent.addEventListener('focusin', () => {
          if (dropdownContent.classList.contains('active')) return;

          const isNestedInOpenDropdown = allDropdownParents.some(otherParent => {
            if (otherParent === parent || otherParent.contains(parent)) return false;
            const otherContent = otherParent.classList.contains('dropdown-menu')
              ? otherParent.querySelector('.list-menu')
              : (otherParent.nextElementSibling?.classList?.contains('list-menu') ? otherParent.nextElementSibling : otherParent.querySelector('.list-menu'));
            return !!otherContent && otherContent !== dropdownContent && otherContent.contains(parent) && otherContent.classList.contains('active');
          });

          if (!isNestedInOpenDropdown) {
            this.closeAllDropdowns();
            this.closeMegaMenu();
          }
          openDropdown();
        });

        parent.addEventListener('focusout', (e) => {
          this.clearTimers(parent);
          if (e.relatedTarget && !dropdownContent.contains(e.relatedTarget)) {
            this.updateButtonState(parent, false);
            if (!this.inlineMenu.contains(e.relatedTarget)) {
              this.closeAllDropdowns();
              this.closeMegaMenu();
            }
          }
        });

        dropdownContent.addEventListener('focusout', (e) => {
          this.clearTimers(parent);
          if (e.relatedTarget && !dropdownContent.contains(e.relatedTarget) && !parent.contains(e.relatedTarget)) {
            closeDropdown();
            this.updateButtonState(parent, false);
          } else if (!this.inlineMenu.contains(e.relatedTarget)) {
            closeDropdown();
            this.updateButtonState(parent, false);
          }
        });
      });
    }

    clearTimers(el) {
      if (el._openTimer) {
        clearTimeout(el._openTimer);
        el._openTimer = null;
      }
      if (el._closeTimer) {
        clearTimeout(el._closeTimer);
        el._closeTimer = null;
      }
    }

    closeAllDropdowns() {
      this.allListMenus.forEach(dropdown => {
        const parent = dropdown.closest('.has-dropdown, .dropdown-menu');
        if (parent) {
          this.clearTimers(parent);
        }
        if (!dropdown.classList.contains('active')) return;
        dropdown.classList.remove('active');
        Object.assign(dropdown.style, {
          height: '', overflow: '', opacity: '', transform: '', visibility: '', display: ''
        });
        if (dropdown._heightAnimation) {
          dropdown._heightAnimation.cancel();
          dropdown._heightAnimation = null;
        }
      });

      this.inlineMenu?.querySelectorAll('.has-dropdown, .dropdown-menu').forEach(btn => {
        this.updateButtonState(btn, false);
      });

      if (this.header) {
        this.header.classList.remove('has-hovered-dropdown');
      }
    }

    getMenuHeight(menuIndex) {
      if (this.menuHeights[menuIndex]) {
        return this.menuHeights[menuIndex];
      }

      const content = document.querySelector(`[data-menu-content="${menuIndex}"]`);
      if (!content) return 0;

      const originalStyles = {
        position: content.style.position,
        visibility: content.style.visibility,
        display: content.style.display
      };

      Object.assign(content.style, {
        position: 'fixed',
        visibility: 'hidden',
        display: 'block'
      });

      if (content.hasAttribute('data-has-badges')) {
        content.querySelectorAll('menu-badges').forEach(b => {
          const selfWidth = b.offsetWidth;
          if (!selfWidth) return;

          const parentLink = b.closest('a');
          if (!parentLink) return;

          if (parentLink.style.getPropertyValue('--badge-space')) return;

          const width = selfWidth + 15;
          const height = b.offsetHeight + 14;
          let prevWidth = b.previousElementSibling?.offsetWidth || 0;
          if (b.previousElementSibling) prevWidth += 10;

          let shiftX = parentLink.offsetWidth - prevWidth - width + 15;
          if (shiftX < 0) shiftX = 0;

          parentLink.style.setProperty('--badge-space', `${width}px`);
          parentLink.style.setProperty('--badge-height', `${height}px`);
          parentLink.style.setProperty('--shiftX', `${shiftX}px`);

          if (b._updateRaf) cancelAnimationFrame(b._updateRaf);
          if (b._updateTimer) clearTimeout(b._updateTimer);
        });
      }

      const height = content.getBoundingClientRect().height;
      Object.assign(content.style, originalStyles);

      this.menuHeights[menuIndex] = height;
      return height;
    }

    animateDropdown(dropdownContent, isClosing = false) {
      if (!this.motion.animate) return;
      if (dropdownContent._heightAnimation) {
        dropdownContent._heightAnimation.cancel();
      }

      const baseDuration = this.animationConfig.duration;

      if (isClosing) {
        const currentHeight = dropdownContent.offsetHeight;
        const duration = Math.min(baseDuration * 1.5,
          Math.max(0.25, (currentHeight / 300) * baseDuration));

        dropdownContent._heightAnimation = this.motion.animate(dropdownContent, {
          opacity: [1, 0]
        }, {
          duration: duration * 0.5,
          easing: this.animationConfig.easing
        });

        dropdownContent._heightAnimation.finished.finally(() => {
          Object.assign(dropdownContent.style, {
            height: '',
            overflow: '',
            opacity: ''
          });
          dropdownContent._heightAnimation = null;
        });

      } else {
        const realHeight = this.getDropdownHeight(dropdownContent);
        const duration = Math.min(baseDuration * 1.5, Math.max(0.25, (realHeight / 300) * baseDuration));
        const startHeight = realHeight * 0.75;

        requestAnimationFrame(() => {
          dropdownContent._heightAnimation = this.motion.animate(dropdownContent, {
            height: [`${startHeight}px`, `${realHeight}px`],
            opacity: [0.9, 1]
          }, {
            duration: duration,
            easing: this.animationConfig.easing
          });

          dropdownContent._heightAnimation.finished.finally(() => {
            Object.assign(dropdownContent.style, {
              height: '',
              overflow: ''
            });
            dropdownContent._heightAnimation = null;
          });
        });
      }
    }

    positionDropdown(parent, dropdownContent) {
      Object.assign(dropdownContent.style, {
        visibility: 'hidden',
        opacity: '0',
        display: 'block',
        height: 'auto'
      });

      const parentRect = parent.getBoundingClientRect();
      const dropdownWidth = this.getDropdownWidth(dropdownContent);
      const spaceRight = window.innerWidth - parentRect.right - 30;
      const spaceLeft = parentRect.left;

      dropdownContent.classList.toggle(
        'left-side',
        dropdownWidth > spaceRight && (spaceLeft > dropdownWidth || spaceLeft > spaceRight)
      );

      Object.assign(dropdownContent.style, {
        visibility: '',
        opacity: '',
        display: '',
        height: ''
      });
    }

    getDropdownHeight(dropdownContent) {
      const cached = this.dropdownHeightCache.get(dropdownContent);
      if (cached !== undefined) {
        return cached;
      }
      const originalStyles = {
        height: dropdownContent.style.height,
        overflow: dropdownContent.style.overflow,
        display: dropdownContent.style.display
      };

      Object.assign(dropdownContent.style, {
        height: 'auto',
        overflow: 'hidden',
        display: 'block'
      });

      const height = dropdownContent.offsetHeight;
      Object.assign(dropdownContent.style, originalStyles);

      this.dropdownHeightCache.set(dropdownContent, height);
      return height;
    }

    getDropdownWidth(dropdownContent) {
      const cached = this.dropdownWidthCache.get(dropdownContent);
      if (cached && cached.width === window.innerWidth) {
        return cached.value;
      }

      const value = dropdownContent.offsetWidth;
      this.dropdownWidthCache.set(dropdownContent, {
        width: window.innerWidth,
        value
      });
      return value;
    }

    closeOtherPopup() {
      document.querySelectorAll('localization-form, predictive-search, details-modal').forEach(element => {
        if (typeof element.forceClose === 'function') {
          element.forceClose();
        }
      });
    }

    forceClose() {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }

      this.isHovering = false;

      removeTrapFocus?.();

      const wasBlocked = this.classList.contains('mega-menu--block');

      if (this.currentOpenMenu !== null) {
        this.classList.remove('mega-menu--block');
        document.body.removeAttribute('data-mega-menu-block');

        const dropdownContent = this.menuContainer;

        if (dropdownContent._heightAnimation) {
          dropdownContent._heightAnimation.cancel();
        }

        dropdownContent.classList.remove('open', 'has-scroll');
        dropdownContent.style.height = '';
        dropdownContent.style.opacity = '';
        if (this.menuBackdrop) {
          this.menuBackdrop.style.opacity = '';
          this.menuBackdrop.style.display = 'none';
        }
        this.menuContainerParent.style.opacity = '';
        this.menuContainerParent._isClosing = false;

        const menuVideos = dropdownContent.querySelectorAll('deferred-media');
        menuVideos.forEach(video => {
          this.pauseDeferredMedia(video);
          this.restartDeferredMedia(video);
        });

        if (wasBlocked) {
          this.classList.add('mega-menu--block');
          document.body.setAttribute('data-mega-menu-block', 'true');
        }
      }

      this.allListMenus.forEach(dropdown => {
        if (!dropdown.classList.contains('active')) return;
        dropdown.classList.remove('active');
        Object.assign(dropdown.style, {
          height: '', overflow: '', opacity: '', transform: '', visibility: '', display: ''
        });

        if (dropdown._heightAnimation) {
          dropdown._heightAnimation.cancel();
          dropdown._heightAnimation = null;
        }
      });

      this.updateButtonState(null, false, true);

      this.allDropdownButtons.forEach(button => {
        this.updateButtonState(button, false);
      });

      if (this.header) {
        this.header.classList.remove('has-hovered-megamenu', 'has-hovered-dropdown');
      }

      this.allMenuContents.forEach(content => {
        content.classList.remove('active');
      });

      this.currentOpenMenu = null;
      this.openedByKeyboard = false;
    }

    disconnectedCallback() {
      if (this.handleWindowResize) {
        window.removeEventListener('resize', this.handleWindowResize);
      }
      if (this.idlePreloadHandle) {
        if ('cancelIdleCallback' in window) {
          window.cancelIdleCallback(this.idlePreloadHandle);
        } else {
          clearTimeout(this.idlePreloadHandle);
        }
      }
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      if (this.resizeDebounce) clearTimeout(this.resizeDebounce);
      if (this.initialLayoutRaf) cancelAnimationFrame(this.initialLayoutRaf);
      this.badgesPositionTimers?.forEach((timer) => clearTimeout(timer));
      this.badgesPositionTimers = new Map();

      this.menuHeights = {};
    }
  });
}